/* eslint-disable  */
import {
	useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useDispatch } from 'react-redux';
import {
	BarChart3,
	Filter, SquareArrowOutUpRight, WorkflowIcon, RefreshCcw, Loader2
} from 'lucide-react';
import { BackdropLoader } from '@/Components/Loaders';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import GlobalSearch from '@/Components/globalSearch/search';
import drawerActions from '@/Actions/drawer.actions';
import { filtersInfoChip, removeKeyFromUrl, unit_map, useQuery } from '@/Helpers';
import { parentTabs } from './constants';
import TableWithTabs from '../AIFMSUMMARY/TableWithTabs';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { useTableState } from './hooks/useTableState';
import { useAiFmsData } from './hooks/useAiFmsData';
import { useTableData } from './hooks/useTableData';
import moment from 'moment';
import StatsComp from '../ProductionAnalysis/StatsComp';
import DatePickerCustom from '@/Components/DatePickerCustom/DatePickerCustom';
import BatchPicker from '@/Components/BatchPicker/BatchPicker';
import { useAuth } from '@/Components/Auth/Login/auth';

const exportInfo = {
	showExportBtn: true,
};

const LoadingSpinner = (isLoading) => (
	<BackdropLoader isLoading={isLoading} showProgress={isLoading} />
);

const extractUnit = (valueString) => {
	if (!valueString || typeof valueString !== 'string') return '';
	const match = valueString.match(/[\d.]+\s*([a-zA-Z]+)/);
	return match ? match[1] : '';
};

// Helper to format number to 3 decimal places, returns empty string if invalid
const formatNum = (value, decimals = 3) => {
	const num = parseFloat(value);
	return isNaN(num) ? '' : num.toFixed(decimals);
};

// Helper to calculate shortfall (ordered - fulfilled) if positive
const calcShortfall = (ordered, fulfilled, isFixed = true) => {
	const o = parseFloat(ordered) || 0;
	const f = parseFloat(fulfilled) || 0;
	return o > f ? (isFixed ? (o - f).toFixed(3) : o - f) : '';
};

function AiFmsDynamicPage() {
	const dispatch = useDispatch();
	const { appConfig = {} } = useAuth();
	const isNagaCustomer = appConfig?.customerId === 72;
	const isDanaChogaCustomer = appConfig?.customerId === 70;
	const params = useQuery();
	const parentValue = params.get('parentValue');
	const [filters, setFilters] = useState({});
	const [search, setSearch] = useState({});
	const [singleDate, setSingleDate] = useState(null);
	const [selectedBatches, setSelectedBatches] = useState([]);
	const tableWithTabsRef = useRef(null);

	const parentTabList = useMemo(() => {
		return parentTabs;
	}, []);

	const {
		rawData,
		statsSummary,
		isLoading,
		fetchData,
		fetchCogsAnalysisGraph,
		cacheTime,
		refresh,
		note,
	} = useAiFmsData({
		parentTab: parentTabList[0],
	});

	const {
		parentTab,
		active,
		selectedTab,
		tableTab,
		mealTabsList,
		data,
		setSelectedTab,
		handleParentTab,
		handleMealTab,
		handleTableTab,
		getTabInfo,
		getAvailableTabs,
		parentTabVersion,
		setSelectSubTab,
		selectSubTab,
		setPerUnit,
		perUnit
	} = useTableState([], setSearch, isNagaCustomer, isDanaChogaCustomer, parentTabList);

	const {
		getTableInfo,
		dropDownItems,
	} = useTableData(rawData, parentTab, search, isLoading, selectedTab);

	useEffect(() => {
		if (parentValue) {
			const cleanParentValue = parentValue.replace(/^"(.*)"$/, '$1');
			const foundParentTab = parentTabList.find(tab => tab.value === cleanParentValue);
			if (foundParentTab) {
				handleParentTab(foundParentTab);
			}
		}
		removeKeyFromUrl('parentValue');
	}, [handleParentTab]);


	const filtersInfo = {
		header: {
			store: false, date: false, product: false, process: false,
		},
		kitchenBaseStore: {
			text: 'Kitchen ', show: true,
		},
		productWithVariants: {
			text: 'Product',
			show: true,
		},
		pods: {
			text: 'Pods', show: false,
		},
		allProcessType: {
			text: 'All Process Type', show: true, multiSelect: true,
		},
		dateRange: {
			text: parentTab?.value?.toLowerCase() === 'dispatched' ? 'Dispatched At' : 'Scheduled At', show: true,
		},
		orderType: {
			text: 'Tab Type',
		},
	};

	const handleParentTabChange = useCallback(async (tab) => {
		handleParentTab(tab);
		setSearch({});
		setSelectedTab('weight');
		setSingleDate(null);
		setSelectedBatches([]);
	}, [handleParentTab, filters, fetchData, active, selectedTab, tableTab]);

	const getEffectiveFilters = useCallback((currentFilters, date, batches) => {
		let newFilters = { ...currentFilters };
		if (date && currentFilters?.dateRange) {
			newFilters = {
				...newFilters,
				dateRange: {
					...currentFilters.dateRange,
					from: date,
					to: date,
				}
			};
		}
		if (batches && batches.length > 0) {
			newFilters = {
				...newFilters,
				batchIds: batches.map((b) => b.grnItemBatchId),
			};
		}
		return newFilters;
	}, []);

	const handleMealTabChange = useCallback((tab) => {
		handleMealTab(tab);
		setSearch({});
		if (filters.dateRange) {
			const effectiveFilters = getEffectiveFilters(filters, singleDate, selectedBatches);
			fetchData({ ...effectiveFilters, tableTab, parentTab, selectedTab, shiftId: tab?.id });
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, tab?.id, getSelectedTab, parentTab?.value.toLowerCase());
		}
	}, [handleMealTab, filters, tableTab, parentTab, selectedTab, singleDate, selectedBatches, getEffectiveFilters]);

	const handleSetSelectedTab = useCallback((tabId) => {
		setSelectedTab(tabId);
		setSearch({});
		const availableTabs = getAvailableTabs();
		const selectedMainTab = availableTabs.find(tab => tab.id === tabId);
		let defaultSubTabId = null;

		if (selectedMainTab && selectedMainTab.subTabs) {
			const defaultSubTab = selectedMainTab.subTabs.find(sub => sub.default);
			if (defaultSubTab) {
				defaultSubTabId = defaultSubTab.id;
				setSelectSubTab(defaultSubTabId);
			}
		} else {
			setSelectSubTab(null);
		}
		if (filters.dateRange) {
			const isPerUnit = tabId === 'cost' && defaultSubTabId === 'perUnit';
			setPerUnit(isPerUnit);
			const effectiveFilters = getEffectiveFilters(filters, singleDate, selectedBatches);
			fetchData({ ...effectiveFilters, tableTab, parentTab, selectedTab: tabId, shiftId: active?.id, isPerUnit });
			const getSelectedTab = tabId === 'weight' ? 'TotalWeight' : tabId === 'cost' ? 'TotalCost' : tabId === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase());
		}
	}, [setSelectedTab, filters, parentTab, tableTab, active, getAvailableTabs, singleDate, selectedBatches, getEffectiveFilters]);

	const handleSetSelectedSubTab = useCallback((subTabId) => {
		setSelectSubTab(subTabId);
		setSearch({});
		if (filters.dateRange) {
			const isPerUnit = selectedTab === 'cost' && subTabId === 'perUnit';
			setPerUnit(isPerUnit);
			const effectiveFilters = getEffectiveFilters(filters, singleDate, selectedBatches);
			fetchData({ ...effectiveFilters, tableTab, parentTab, selectedTab, shiftId: active?.id, isPerUnit });
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase());
		}
	}, [setSelectedTab, filters, parentTab, tableTab, active, selectedTab, selectSubTab, singleDate, selectedBatches, getEffectiveFilters]);


	const handleDataFetch = useCallback((filter) => {
		const processedFilters = { ...filter };
		if (processedFilters?.store) {
			processedFilters.kitchenBaseStore = processedFilters.store;
		}
		if (processedFilters?.dateRange) {
			processedFilters.dateRange.active = true;
		}
		// Reset single date when main filters change
		if (!moment(singleDate).isSame(moment(filter?.dateRange?.from), 'day') && !moment(singleDate).isSame(moment(filter?.dateRange?.to), 'day')) {
			setSingleDate(null);
			setSelectedBatches([]);
		}
		setFilters(processedFilters);
	}, [parentTab?.id, tableTab, active, selectedTab, singleDate]);

	useEffect(() => {
		if (filters?.dateRange && tableTab && parentTab && parentTabVersion > 0) {
			const effectiveFilters = getEffectiveFilters(filters, singleDate, selectedBatches);
			const isPerUnit = selectedTab === 'cost' && selectSubTab === 'perUnit';
			setPerUnit(isPerUnit);
			fetchData({ ...effectiveFilters, tableTab, parentTab, shiftId: active?.id, selectedTab, isPerUnit });
		}
	}, [parentTabVersion, filters, singleDate, selectedBatches]);

	const handleSingleDateChange = useCallback((date) => {
		setSingleDate(date);
		setSelectedBatches([]);
		if (filters?.dateRange) {
			const effectiveFilters = getEffectiveFilters(filters, date, []);
			fetchData({ ...effectiveFilters, tableTab, parentTab, shiftId: active?.id, selectedTab });

			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase());
		}
	}, [filters, tableTab, parentTab, active, selectedTab, fetchData, fetchCogsAnalysisGraph, getEffectiveFilters]);

	const exportData = () => {
		const headers = [];
		let csvData = [];
		if (isDanaChogaCustomer && !['wastage'].includes(parentTab?.value.toLowerCase()) && selectedTab !== 'qrCount') {
			const isDispatched = ['dispatched'].includes(parentTab?.value.toLowerCase());
			const isPrimaryPackaging = ['primary_packaging'].includes(parentTab?.value.toLowerCase());
			rawData?.columns?.forEach((column, index) => {
				if (index === 0 && column?.label?.toLowerCase() !== 'total') {
					headers.push(column?.label);
					if (selectedTab === 'weight') {
						headers.push('Output Unit');
					}
					headers.push('Unit');
				} else if (column?.label?.toLowerCase() === 'ega') {
					headers.push(column?.label);
				} else if (column?.label?.toLowerCase() !== 'total') {
					if (selectedTab === 'cost' || selectedTab === 'count') {
						headers.push(column?.label, column?.label, column?.label);
					} else {
						headers.push(column?.label, column?.label, column?.label, column?.label, column?.label, column?.label);
					}
				}
			});

			if (selectedTab === 'cost' || selectedTab === 'count') {
				headers.push('Total', 'Total', 'Total');
			} else {
				headers.push('Total', 'Total', 'Total', 'Total', 'Total', 'Total');
			}

			const subHeaders = [];
			rawData?.columns?.forEach((column, index) => {
				if (index === 0 && column?.label?.toLowerCase() !== 'total') {
					subHeaders.push('');
					if (selectedTab === 'weight') {
						subHeaders.push('');
					}
					subHeaders.push('');
				} else if (column?.label?.toLowerCase() === 'ega') {
					subHeaders.push('');
				} else if (column?.label?.toLowerCase() !== 'total') {
					if (selectedTab === 'cost') {
						subHeaders.push('Ordered Cost', 'Fulfilled Cost', 'Shortfall Cost');
					} else if (selectedTab === 'count') {
						subHeaders.push('Ordered Qty', 'Fulfilled Qty', 'Shortfall Qty');
					} else if (selectedTab === 'weight' && !isDispatched) {
						subHeaders.push('Ordered Weight', 'Fulfilled Weight', 'Ordered Qty', 'Fulfilled Qty', 'Fulfilled Fall Short Weight', 'Fulfilled Fall Short Qty');
					} else if (isDispatched) {
						subHeaders.push('Ordered Weight', 'dispatched Weight', 'Ordered Qty', 'dispatched Qty', 'Dispatched Fullfiled Fall Short Weight', 'Dispatched Fullfiled Fall Short Qty');
					} else {
						subHeaders.push('Ordered Weight', 'Fulfilled Weight', 'Ordered Qty', 'Fulfilled Qty', 'Fulfilled Fall Short Weight', 'Fulfilled Fall Short Qty');
					}
				}
			});

			if (selectedTab === 'cost') {
				subHeaders.push('Total Ordered Cost', 'Total Fulfilled Cost', 'Total Shortfall Cost');
			} else if (selectedTab === 'count') {
				subHeaders.push('Total Ordered Qty', 'Total Fulfilled Qty', 'Total Shortfall Qty');
			} else {
				subHeaders.push('Total Ordered Weight', 'Total Fulfilled Weight', 'Total Ordered Qty', 'Total Fulfilled Qty', 'Total Fall Short Weight', 'Total Fall Short Qty');
			}
			const rows = rawData?.data?.map((item) => {
				const row = [];
				// Initialize totals
				let totalOrdered1 = 0;
				let totalFulfilled1 = 0;
				let totalShortfall1 = 0;

				let totalOrderedWeight = 0;
				let totalFulfilledWeight = 0;
				let totalOrderedQty = 0;
				let totalFulfilledQty = 0;
				let totalShortFallWeight = 0;
				let totalShortFallQty = 0;
				rawData?.columns?.forEach((header, index) => {
					const cellData = item?.[header?.key];

					if (index === 0 && header?.label?.toLowerCase() !== 'total') {
						const value = (cellData && typeof cellData === 'object' && cellData.value !== undefined)
							? cellData.value
							: (cellData ?? '');
						const splitValue = value.includes('¦') ? value.split('¦')[0] : value;
						const splitUnit = value.includes('¦') ? value.split('¦')[1] : null;
						row.push(splitValue);
						if (selectedTab === 'weight') {
							row.push("kg");
						}
						row.push(splitUnit ?? "-");
					} else if (header?.label?.toLowerCase() === 'ega') {
						row.push(cellData?.value ?? '-');
					} else if (header?.label?.toLowerCase() !== 'total') {
						if (cellData && typeof cellData === 'object') {
							const unit = extractUnit(cellData?.value)?.toLowerCase();
							const isKg = unit === 'kg';
							const isPcs = unit === 'pcs';

							if (selectedTab === 'cost') {
								const ord = cellData.orderedCost ?? 0;
								const full = cellData.dispatchedCost ?? cellData.fullfilledCost ?? 0;
								const short = calcShortfall(ord, full);

								row.push(formatNum(ord));
								row.push(formatNum(full));
								row.push(short);

								totalOrdered1 += Number(ord);
								totalFulfilled1 += Number(full);

							} else if (selectedTab === 'count') {
								// Parse "Fulfilled / Ordered" string e.g., "129.00 pcs / 130.00 pcs"
								let ord = 0;
								let full = 0;
								const valStr = cellData.value || "";
								if (valStr.includes('/')) {
									const parts = valStr.split('/');
									full = parseFloat(parts[0]?.replace(",", '')) || 0;
									ord = parseFloat(parts[1]?.replace(",", '')) || 0;
								} else {
									ord = cellData.orderedQuantity ?? 0;
									full = cellData.dispatchedQuantity ?? cellData.gross_qty ?? cellData.grossProducedQuantity ?? 0;
								}
								const short = calcShortfall(ord, full, false);

								row.push(formatNum(ord));
								row.push(formatNum(full));
								row.push(short);

								totalOrdered1 += Number(ord);
								totalFulfilled1 += Number(full);

							} else {
								// Weight Logic (Existing)
								let fulfilledWeight = '';
								let orderedWeight = '';
								let fulfilledQty = '';
								let orderedQty = '';
								let fulfilledFallShortWeight = '';
								let fulfilledFallShortQty = '';

								if (isDispatched) {
									orderedWeight = isKg ? formatNum(cellData.orderedQuantity) : '';
									fulfilledWeight = isKg ? formatNum(cellData.dispatchedQuantity) : '';
									orderedQty = orderedWeight;
									fulfilledQty = fulfilledWeight;
									fulfilledFallShortWeight = isKg ? calcShortfall(cellData.orderedQuantity, cellData.dispatchedQuantity) : '';
									// fulfilledFallShortQty = isPcs ? calcShortfall(cellData.orderedQuantity, cellData.dispatchedQuantity) : '';
									fulfilledFallShortQty = fulfilledFallShortWeight;
								} else {
									const valStr = cellData.value || "";
									if (valStr.includes('/')) {
										const parts = valStr.split('/');
										fulfilledWeight = parseFloat(parts[0]?.replace(",", '')) || 0;
										fulfilledQty = fulfilledWeight
										orderedWeight = parseFloat(parts[1]?.replace(",", '')) || 0;
										orderedQty = orderedWeight
										fulfilledFallShortWeight = calcShortfall(orderedQty, fulfilledQty);
										fulfilledFallShortQty = calcShortfall(orderedQty, fulfilledQty);
									}
									// orderedWeight = formatNum(cellData.orderedWeight);
									// fulfilledWeight = formatNum(cellData.producedWeight);
									// orderedQty = formatNum(cellData.orderedQuantity);
									// fulfilledQty = formatNum(cellData.gross_qty) || formatNum(cellData.grossProducedQuantity);
									// fulfilledFallShortWeight = calcShortfall(cellData.orderedWeight, cellData.producedWeight);
									// fulfilledFallShortQty = calcShortfall(cellData.orderedQuantity, cellData.gross_qty);
								}

								const addNum = (val) => (val !== '' && !isNaN(Number(val))) ? Number(val) : 0;
								totalOrderedWeight += addNum(orderedWeight);
								totalFulfilledWeight += addNum(fulfilledWeight);
								totalOrderedQty += addNum(orderedQty);
								totalFulfilledQty += addNum(fulfilledQty);
								totalShortFallWeight += addNum(fulfilledFallShortWeight);
								totalShortFallQty += addNum(fulfilledFallShortQty);
								row.push(orderedWeight, fulfilledWeight, orderedQty, fulfilledQty, fulfilledFallShortWeight, fulfilledFallShortQty);
							}
						} else {
							if (selectedTab === 'cost' || selectedTab === 'count') {
								row.push('', '', '');
							} else {
								row.push('', '', '', '', '', '');
							}
						}
					}
				});

				// Totals Row Push
				if (selectedTab === 'cost' || selectedTab === 'count') {
					totalShortfall1 = totalOrdered1 - totalFulfilled1;
					row.push(
						formatNum(totalOrdered1),
						formatNum(totalFulfilled1),
						formatNum(totalShortfall1)
					);
				} else {
					row.push(
						formatNum(totalOrderedWeight),
						formatNum(totalFulfilledWeight),
						formatNum(totalOrderedQty),
						formatNum(totalFulfilledQty),
						formatNum(totalShortFallWeight),
						formatNum(totalShortFallQty)
					);
				}

				return row;
			});

			csvData = [headers, subHeaders, ...rows];
		}
		else {
			rawData?.columns?.forEach((column, index) => {
				headers.push(column?.label);
				if (index === 0 && rawData?.data[1]?.[rawData?.columns[0]?.key]?.value?.includes('¦')) {
					headers.push('Unit');
				}
			});
			const rows = rawData?.data?.map((item) => {
				const row = [];
				rawData?.columns?.forEach((header, index) => {
					const cellData = item?.[header?.key];
					const value = (cellData && typeof cellData === 'object' && cellData.value !== undefined)
						? cellData.value
						: (cellData ?? '');
					const splitValue = index === 0 && value.includes('¦') ? value.split('¦')[0] : value;
					const splitUnit = index === 0 && value.includes('¦') ? value.split('¦')[1] : null;
					row.push(splitValue);
					if (index == 0 && rawData?.data[1]?.[rawData?.columns[0]?.key]?.value?.includes('¦')) {
						row.push(splitUnit ?? "-");
					}
				});
				return row;
			});
			csvData = [headers, ...rows];
		}

		const csvContent = csvData.map((row) => row.join(',')).join('\n');
		const csvFile = new Blob([csvContent], { type: 'text/csv' });
		const downloadLink = document.createElement('a');
		downloadLink.download = `AI_FMS_SUMMARY-${moment().format('YYYY-MM-DD')}.csv`;
		downloadLink.href = window.URL.createObjectURL(csvFile);
		downloadLink.style.display = 'none';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		window.URL.revokeObjectURL(downloadLink.href);
		document.body.removeChild(downloadLink);
	}

	useEffect(() => {
		if (filters?.dateRange) {
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(filters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase());
		}
	}, [filters?.dateRange, parentTab?.id]);

	const FilterSection = useMemo(() => (
		<div className='flex flex-1 items-baseline gap-3'>
			<div className='flex-1 min-w-0'>
				<GlobalSearch
					placeholderText='Search'
					dropDownOptions={dropDownItems}
					searchDropDown={dropDownItems?.length > 0}
					setSelectedValue={setSearch}
				/>
			</div>
			<div className='flex-shrink-0'>
				{filtersInfoChip(filtersInfo, filters)}
			</div>
			<Button
				onClick={() => dispatch(drawerActions.toggleFiltersDrawer(true, {
					handleDataExport: (filtersState) => {
						exportData();
					},
				}))}
				className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 flex-shrink-0'
			>
				<span>Filter & Export</span>
				<Filter size={16} />
			</Button>
		</div>
	), [filters, dispatch, parentTab, rawData, selectedTab, selectSubTab, active]);

	const ParentTabsSection = useMemo(() => (
		<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033]'>
			{parentTabList?.map((ele) => {
				const isActive = parentTab?.id === ele?.id;
				return (
					<div
						key={ele.id}
						className={`flex items-center gap-2 justify-center px-3 py-2 text-[11.5px] font-medium cursor-pointer whitespace-nowrap
              ${isActive ? 'bg-[#ffb703]' : ''}`}
						onClick={() => handleParentTabChange(ele)}
					>
						<img src={ele?.icon} alt="icon" className="w-6 h-6" />
						{ele?.header}
					</div>
				);
			})}
		</div>
	), [parentTab, handleParentTabChange]);

	const MealTabsSection = useMemo(() => (
		<div className='inline-flex overflow-x-auto max-w-[27rem] rounded-md bg-[#FDE6AD] border border-[#00000033]'>
			{mealTabsList.map((ele) => {
				const isActive = active?.id === ele?.id;
				return (
					<button
						key={ele.id}
						className={`px-3.5 py-2 text-sm font-medium rounded-md whitespace-nowrap ${isActive
							? 'bg-yellow-400 text-black'
							: 'text-gray-700 hover:bg-yellow-300/50'
							}`}
						onClick={() => handleMealTabChange(ele)}
					>
						{ele?.shiftName}
					</button>
				);
			})}
		</div>
	), [mealTabsList, active, handleMealTab]);

	const subMealTabsSection = useMemo(() => {
		const availableTabs = getAvailableTabs();
		const selectedMainTab = availableTabs.find(tab => tab.id === selectedTab);
		// console.log(parentTab?.value,parentTab?.value !== 'PRODUCED' && parentTab?.value !== 'SECONDARY_PACKAGING' && parentTab?.value !== 'PRIMARY_PACKAGING', " ccc ");
		if (parentTab?.value !== 'PRODUCED' && parentTab?.value !== 'SECONDARY_PACKAGING' && parentTab?.value !== 'PRIMARY_PACKAGING') return;
		return (
			<div className='inline-flex rounded-md bg-[#FDE6AD] border border-[#00000033]'>
				{selectedMainTab && selectedMainTab.subTabs && Array.isArray(selectedMainTab.subTabs) && (
					selectedMainTab.subTabs.map((subTab) => (
						<button
							key={subTab.id}
							onClick={() => handleSetSelectedSubTab(subTab.id)}
							className={`px-3.5 py-2 flex items-center text-sm font-medium rounded-md whitespace-nowrap ${selectSubTab === subTab.id
								? 'bg-yellow-400 text-black'
								: 'text-gray-700 hover:bg-yellow-300/50'
								}`}
						>
							<span className='mr-1'>{subTab.icon}</span>
							{subTab.lable}
						</button>
					))
				)}
			</div>
		);
	}, [getAvailableTabs, selectedTab, setSelectedTab, selectSubTab, parentTab?.value]);

	const TabsSection = useMemo(() => (
		<div className='inline-flex rounded-md bg-[#FDE6AD] border border-[#00000033]'>
			{getAvailableTabs().map((tab) => (
				<button
					key={tab.id}
					onClick={() => handleSetSelectedTab(tab.id)}
					disabled={tab.disabled}
					title={tab.tooltip || ''}
					className={`px-3.5 py-2 flex items-center text-sm font-medium rounded-md whitespace-nowrap ${tab.disabled
						? 'text-gray-400 cursor-not-allowed opacity-60 bg-transparent'
						: selectedTab === tab.id
							? 'bg-yellow-400 text-black'
							: 'text-gray-700 hover:bg-yellow-300/50'
						}`}
				>
					<span className='mr-1'>{tab.icon}</span>
					{tab.label}
				</button>
			))}
		</div>
	), [getAvailableTabs, selectedTab, setSelectedTab, parentTab?.value, handleSetSelectedTab]);

	const renderCustomLegend = useCallback(() => {
		return (
			<div className='flex items-center gap-4 text-[11px] font-medium'>
				<div className='flex items-center gap-1.5'>
					<span className='w-3 h-3 rounded-full bg-black' />
					<span className='text-gray-700'>No Scanning Done</span>
				</div>
				<div className='flex items-center gap-1.5'>
					<span className='w-3 h-3 rounded-full bg-red-500' />
					<span className='text-gray-700'>Outside threshold (5%)</span>
				</div>
			</div>
		);
	}, []);

	const [highlightCache, setHighlightCache] = useState(false);

	useEffect(() => {
		if (!isLoading && cacheTime && cacheTime !== 'Just now') {
			setHighlightCache(true);
			const timer = setTimeout(() => {
				setHighlightCache(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [cacheTime, isLoading]);

	useEffect(() => {
		if (!getTableInfo?.()?.data?.length) return;
		setTimeout(() => {
			const tableEl = tableWithTabsRef.current;
			if (!tableEl) return;
			const pageHasScroll = document.documentElement.scrollHeight > document.documentElement.clientHeight;
			if (pageHasScroll) {
				tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	}, [getTableInfo?.()?.data]);

	return (
		<div className='p-2'>
			<style>
				{`
					@keyframes shake-tilt {
						0% { transform: rotate(0deg); }
						25% { transform: rotate(-5deg); }
						50% { transform: rotate(5deg); }
						75% { transform: rotate(-5deg); }
						100% { transform: rotate(0deg); }
					}
					.animate-shake-tilt {
						animation: shake-tilt 0.2s ease-in-out 1s 2;
					}
				`}
			</style>
			<ImagesFinder
				filtersInfo={filtersInfo}
				exportInfo={exportInfo}
				onSubmit={handleDataFetch}
				disableFutureDate
				hideDateRangeTimeType
			/>

			<>
				<div className='flex flex-col gap-2'>
					<div className='flex justify-between items-center'>
						<h2 className='font-bold text-lg'>Operations Summary</h2>
						<div className='flex items-center gap-2'>
							<Button
								className="flex items-center gap-2 bg-white text-yellow-600 hover:bg-white font-semibold text-[14px] transition-colors duration-200"
								onClick={() => window.open(`${urls.EXTENSION}${urls.PRODUCTION_ANALYSIS}?tab=productGroupWise&dateRangeFilter=${encodeURIComponent(JSON.stringify(filters?.dateRange))}`, '_blank')}
							>
								<BarChart3 size={20} className='transition-transform duration-300  hover:scale-110 scale-100' />
								Production Analysis
								<SquareArrowOutUpRight size={18} />
							</Button>
							<Button
								className='flex items-center gap-2 w-fit bg-white text-yellow-600 hover:bg-white font-semibold text-[12px] transition-colors duration-200'
								onClick={() => window.open(`${urls.EXTENSION}${urls.PROCESS_FLOW_NEW}?&store=${filters?.fromPodFilter?.id ? JSON.stringify(filters?.fromPodFilter) : null}&dateOnly=${encodeURIComponent(JSON.stringify({
									date: filters?.dateRange?.to,
								}))}`, '_blank')}
							>
								<WorkflowIcon size={20} />
								<span>View Operation Flow</span>
								<SquareArrowOutUpRight size={18} />
							</Button>
						</div>
					</div>
					{FilterSection}
					<div className='flex flex-col gap-2 mb-2'>
						{ParentTabsSection}
						<div className='flex items-center justify-between'>
							{TabsSection}
							{subMealTabsSection}
							<div className='flex items-center gap-2'>
								{filters?.dateRange && !moment(filters?.dateRange?.from).isSame(moment(filters?.dateRange?.to), 'day') && (
									<DatePickerCustom
										placeholder='Select Date'
										selectedDate={singleDate}
										selectDate={handleSingleDateChange}
										cleanable={true}
										format='yyyy-MM-dd'
										shouldDisableDate={(date) => {
											const from = moment(filters?.dateRange?.from).startOf('day');
											const to = moment(filters?.dateRange?.to).endOf('day');
											return moment(date).isBefore(from) || moment(date).isAfter(to);
										}}
									/>
								)}
								{filters?.dateRange && getTabInfo().tabList?.some((t) => t.batch) && (
									<BatchPicker
										dateRange={singleDate ? { from: singleDate, to: singleDate } : filters.dateRange}
										selectedBatches={selectedBatches}
										setSelectedBatches={setSelectedBatches}
										onBatchSelect={(batch) => {
											if (batch?.costingDate) {
												setSingleDate(new Date(batch.costingDate));
											}
											const { tabList } = getTabInfo();
											const batchTab = tabList.find((t) => t.batch);

											if (batchTab) {
												// Use handleTableTab to switch the View (e.g. processWise, batchproduct)
												handleTableTab(batchTab.value);
											}
										}}
									/>
								)}
							</div>
							{MealTabsSection}
						</div>
					</div>
					<StatsComp statsCards={statsSummary?.map((ele) => ({
						...ele,
						value: ele?.amount,
					}))} />
					{cacheTime && !isLoading && (
						<div className='flex w-full justify-end'>
							<span
								onClick={() => {
									if (isLoading) return;
									refresh();
								}}
								className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm transition-all origin-center
									${isLoading ? 'cursor-not-allowed opacity-50 bg-orange-50 border-orange-200 text-orange-600' : 'cursor-pointer'}
									${highlightCache
										? 'bg-orange-200 border-orange-400 text-orange-800 ring-4 ring-orange-100 shadow-md animate-shake-tilt'
										: 'bg-orange-100 border-orange-200 text-orange-700 hover:bg-orange-200 hover:shadow-md duration-300'
									}`}
							>
								{isLoading ? (
									<Loader2 size={12} className='animate-spin' />
								) : (
									<RefreshCcw size={12} />
								)}
								Last Synced {cacheTime}
							</span>
						</div>
					)}
				</div>
				<TableWithTabs
					tableWithTabsRef={tableWithTabsRef}
					tableInfo={getTableInfo()}
					tabInfo={getTabInfo()}
					height={400}
					isLoading={isLoading}
					renderLegend={renderCustomLegend}
					note={note}
				/>
				{isLoading && <LoadingSpinner isLoading={isLoading} />}
			</>
		</div>
	);
}

export default AiFmsDynamicPage;
